import { useAuth0 } from '@auth0/auth0-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAppConfig } from '@config/AppConfigContext';
import { fetchJson, getAuthHeader, joinUrl } from '@hooks/requests/utils/_partner_api_client';
import { PARTNER_API_KEYS_PATH } from '@hooks/requests/utils/paths';
import type { PartnerApiKey } from '@hooks/requests/queries/use_partner_api_keys_query';

export interface RenameApiKeyParams {
  /**
   * `PartnerApiKey.maskedKey` — not the real client_id, which this
   * frontend never has. Same targeting as revoke: the backend re-resolves
   * the real client_id server-side by matching this masked value's
   * prefix against a fresh read of the org's keys.
   */
  maskedKey: string;
  name: string;
}

/**
 * Rename an existing API key (sc-45257, AC2-AC7). Only the Name field
 * changes — the masked key value, the key's ability to authenticate, and
 * its Client Grant are all untouched (AC4).
 */
export const useRenameApiKeyMutation = () => {
  const { getAccessTokenSilently } = useAuth0();
  const { allyApi, auth0Audience } = useAppConfig();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ maskedKey, name }: RenameApiKeyParams): Promise<PartnerApiKey> => {
      if (!allyApi) throw new Error('Missing allyApi configuration');
      const authHeader = await getAuthHeader(getAccessTokenSilently, auth0Audience);

      return fetchJson<PartnerApiKey>(
        joinUrl(allyApi, `${PARTNER_API_KEYS_PATH}/${encodeURIComponent(maskedKey)}`),
        authHeader,
        'Failed to rename the API key',
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ name }),
        }
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [PARTNER_API_KEYS_PATH] });
    },
  });
};

export default useRenameApiKeyMutation;
